import { BillingType } from './billingTypes';

export const ENGAGEMENT_TYPE = {
  SCOPE_BASED: 'SCOPE_BASED',
  DEDICATED_CAPACITY: 'DEDICATED_CAPACITY',
  DESIGN_AND_FEASIBILITY: 'DESIGN_AND_FEASIBILITY',
  SUPPORT_SERVICES: 'SUPPORT_SERVICES',
};

export const ENGAGEMENT_TYPE_OPTIONS: Dictionary<string> = {
  [ENGAGEMENT_TYPE.SCOPE_BASED]: 'Scope-based',
  [ENGAGEMENT_TYPE.DEDICATED_CAPACITY]: 'Dedicated capacity',
  [ENGAGEMENT_TYPE.DESIGN_AND_FEASIBILITY]: 'Design and feasibility',
  [ENGAGEMENT_TYPE.SUPPORT_SERVICES]: 'Support services',
};
export const PROJECT_SCENARIO = {
  FIXED_SCOPE: 'FIXED_SCOPE',
  DEDICATED_TEAM: 'DEDICATED_TEAM',
  NEW_PROJECT: 'NEW_PROJECT',
  REPLACEMENT: 'REPLACEMENT',
};

export const PROJECT_SCENARIO_OPTIONS = {
  [PROJECT_SCENARIO.FIXED_SCOPE]: 'New project phase (Fixed Scope)',
  [PROJECT_SCENARIO.DEDICATED_TEAM]: 'SOW extension (Dedicated Team)',
  [PROJECT_SCENARIO.NEW_PROJECT]: 'New project for company',
  [PROJECT_SCENARIO.REPLACEMENT]: 'SOW Replacement',
};

export const PROJECT_SCENARIO_HINTS = {
  DEDICATED_TEAM:
    'Continue an existing project by extending the current SOW under a dedicated team model. Use this when the team and engagement setup remain unchanged.',
  FIXED_SCOPE:
    'Create a new SOW for an additional fixed scope phase within an existing project. Use this when new work is defined with specific scope and milestones.',
  NEW_PROJECT: 'Start a new project. Use this when the work is not related to any existing project.',
  REPLACEMENT:
    'Replace the current SOW in an existing project. Use this when the existing agreement needs to be fully updated or superseded.',
} as const;

export const PROJECT_STATE = {
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
};

export const COMMERCIAL_STATE = {
  COMMITTED: 'COMMITTED',
  PRE_COMMITTED: 'PRE_COMMITTED',
  CHANGE_IN_PROGRESS: 'CHANGE_IN_PROGRESS',
  ON_HOLD: 'ON_HOLD',
  ACCEPTANCE_PENDING: 'ACCEPTANCE_PENDING',
  CLOSED: 'CLOSED',
  CANCELLING: 'CANCELLING',
  ACCEPTED: 'ACCEPTED',
  CANCELLED: 'CANCELLED',
};

export const COMMERCIAL_STATE_OPTIONS = {
  [COMMERCIAL_STATE.COMMITTED]: 'Committed',
  [COMMERCIAL_STATE.PRE_COMMITTED]: 'Pre-committed',
  [COMMERCIAL_STATE.CHANGE_IN_PROGRESS]: 'Change in progress',
  [COMMERCIAL_STATE.ON_HOLD]: 'On hold',
  [COMMERCIAL_STATE.ACCEPTANCE_PENDING]: 'Acceptance pending',
  [COMMERCIAL_STATE.CLOSED]: 'Closed',
  [COMMERCIAL_STATE.CANCELLING]: 'Cancelling',
  [COMMERCIAL_STATE.ACCEPTED]: 'Accepted',
  [COMMERCIAL_STATE.CANCELLED]: 'Cancelled',
};

export const PROJECT_FROM_OPPORTUNITY_STATE = {
  PRESTART: 'PRESTART',
  ACTIVE: 'ACTIVE',
  COMPLETED: 'COMPLETED',
  ARCHIVED: 'ARCHIVED',
};

export const OUTCOME_STATUS = {
  ACTIVE: 'ACTIVE',
  COMPLETED: 'COMPLETED',
};
export const OUTCOME_STATUS_OPTIONS = {
  [OUTCOME_STATUS.ACTIVE]: 'Active',
  [OUTCOME_STATUS.COMPLETED]: 'Completed',
};

export const MILESTONE_STATUS = {
  ...OUTCOME_STATUS,
  REJECTED: 'REJECTED',
};
export const MILESTONE_STATUS_OPTIONS = {
  ...OUTCOME_STATUS_OPTIONS,
  [MILESTONE_STATUS.REJECTED]: 'Rejected',
};

export const BILLING_TYPE_LABELS: Record<BillingType, string> = {
  MILESTONE: 'Milestone based',
  PERIODIC_PREPAID: 'Periodic pre-paid',
  PERIODIC_POSTPAID: 'Periodic post-paid',
};

export const BUDGET_STRUCTURE_STATUS = {
  CLOSED: 'CLOSED',
  APPROVED: 'APPROVED',
};

export const BUDGET_STATE = {
  INIT: 'INIT',
  FINALIZED: 'FINALIZED',
};

export const BUDGET_SOURCE = {
  SOW: 'SOW',
  WASTE_REPORT: 'WASTE_REPORT',
  MANUAL_EDIT: 'MANUAL_EDIT',
  CHANGE_REQUEST: 'CHANGE_REQUEST',
  AMENDMENT: 'AMENDMENT',
};

export const CHANGE_REQUEST_BILLING_TYPES = {
  IMMEDIATELY: 'IMMEDIATELY',
  NEXT_BILLING_MILESTONE: 'NEXT_BILLING_MILESTONE',
  SPECIFIC_MILESTONE: 'SPECIFIC_MILESTONE',
};

export const CHANGE_REQUEST_BILLING_OPTIONS_MILESTONE = [
  { value: 'IMMEDIATELY', label: 'Immediately' },
  { value: 'NEXT_BILLING_MILESTONE', label: 'With next billing milestone' },
  { value: 'SPECIFIC_MILESTONE', label: 'With specific billing milestone' },
];

export const CHANGE_REQUEST_BILLING_OPTIONS_PERIODIC = [
  { value: 'IMMEDIATELY', label: 'Immediately' },
  { value: 'NEXT_BILLING_MILESTONE', label: 'With next billing period' },
  { value: 'SPECIFIC_MILESTONE', label: 'With specific billing period' },
];

export const CHANGE_REQUEST_STATUS = {
  DRAFT: 'DRAFT',
  IN_APPROVAL: 'IN_APPROVAL',
  REJECTED: 'REJECTED',
  APPROVED: 'APPROVED',
  SENT: 'SENT',
  VIEWED: 'VIEWED',
  SIGNED: 'SIGNED',
  DECLINED: 'DECLINED',
  CANCELED: 'CANCELED',
};

export const CHANGE_REQUEST_STATUS_OPTIONS = {
  [CHANGE_REQUEST_STATUS.DRAFT]: 'Draft',
  [CHANGE_REQUEST_STATUS.IN_APPROVAL]: 'In approval',
  [CHANGE_REQUEST_STATUS.REJECTED]: 'Rejected',
  [CHANGE_REQUEST_STATUS.APPROVED]: 'Approved',
  [CHANGE_REQUEST_STATUS.SENT]: 'Sent',
  [CHANGE_REQUEST_STATUS.VIEWED]: 'Viewed',
  [CHANGE_REQUEST_STATUS.SIGNED]: 'Signed',
  [CHANGE_REQUEST_STATUS.DECLINED]: 'Declined',
  [CHANGE_REQUEST_STATUS.CANCELED]: 'Cancelled',
};

export const WASTE_STATUS = {
  REGISTERED: 'REGISTERED',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  DRAFT: 'DRAFT',
  CANCELED: 'CANCELED',
  COMPLETELY_DISTRIBUTED: 'COMPLETELY_DISTRIBUTED',
  PARTIALLY_DISTRIBUTED: 'PARTIALLY_DISTRIBUTED',
};

export const WASTE_STATUS_OPTIONS = {
  [WASTE_STATUS.DRAFT]: 'Draft',
  [WASTE_STATUS.REJECTED]: 'Rejected',
  [WASTE_STATUS.APPROVED]: 'Approved',
  [WASTE_STATUS.CANCELED]: 'Canceled',
  [WASTE_STATUS.REGISTERED]: 'Registered',
  [WASTE_STATUS.COMPLETELY_DISTRIBUTED]: 'Approved',
  [WASTE_STATUS.PARTIALLY_DISTRIBUTED]: 'Approved',
};

export const CUSTOMER_REPORT_STATUS = {
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  DRAFT: 'DRAFT',
  CANCELED: 'CANCEL',
  SENT: 'SENT',
  IN_APPROVAL: 'IN_APPROVAL',
};

export const CUSTOMER_REPORT_STATUS_OPTIONS = {
  [CUSTOMER_REPORT_STATUS.DRAFT]: 'Draft',
  [CUSTOMER_REPORT_STATUS.REJECTED]: 'Rejected',
  [CUSTOMER_REPORT_STATUS.APPROVED]: 'Approved',
  [CUSTOMER_REPORT_STATUS.SENT]: 'Sent',
  [CUSTOMER_REPORT_STATUS.CANCELED]: 'Canceled',
  [CHANGE_REQUEST_STATUS.IN_APPROVAL]: 'In approval',
};

export const CUSTOMER_REPORT_FREQUENCY = {
  WEEKLY: 'WEEKLY',
  BIWEEKLY: 'BIWEEKLY',
};

export const REPORT_FREQUENCY_OPTIONS: Dictionary<string> = {
  [CUSTOMER_REPORT_FREQUENCY.WEEKLY]: 'Weekly',
  [CUSTOMER_REPORT_FREQUENCY.BIWEEKLY]: 'Biweekly',
};

export const SYSTEM_REPORT_OPTIONS: Dictionary<string> = {
  true: 'DaVinci',
  false: 'Customer system',
};

export const APPROVAL_TYPE = {
  MANUAL_APPROVAL: 'MANUAL_APPROVAL',
  NO_APPROVAL_REQUIRED: 'NO_APPROVAL_REQUIRED',
  AUTO_APPROVAL_AFTER_3_BUSINESS_DAYS: 'AUTO_APPROVAL_AFTER_3_BUSINESS_DAYS',
} as const;

export const APPROVAL_TYPE_OPTIONS: Dictionary<string> = {
  [APPROVAL_TYPE.MANUAL_APPROVAL]: 'Manual approval only',
  [APPROVAL_TYPE.NO_APPROVAL_REQUIRED]: 'Approval not required',
  [APPROVAL_TYPE.AUTO_APPROVAL_AFTER_3_BUSINESS_DAYS]: 'Manual + auto after 3 business days',
};

export const EXTERNAL_RESOURCE_TYPE = {
  teamsWebUrl: 'teamsWebUrl',
  sharepointWebUrl: 'sharepointWebUrl',
  gitlabWebUrl: 'gitlabWebUrl',
  redmineWebUrl: 'redmineWebUrl',
  jiraWebUrl: 'jiraWebUrl',
  figmaWebUrl: 'figmaWebUrl',
  other: 'other',
};
export const restrictedResourcesForPartialManage = [
  EXTERNAL_RESOURCE_TYPE.teamsWebUrl,
  EXTERNAL_RESOURCE_TYPE.sharepointWebUrl,
  EXTERNAL_RESOURCE_TYPE.gitlabWebUrl,
  EXTERNAL_RESOURCE_TYPE.redmineWebUrl,
];

export const DOCUSIGN_PLACEHOLDERS = {
  client_initials_here_anchor: '<Client Initials>',
  client_date_here_anchor: '<Date>',
  client_sign_here_anchor: '<Client Signature>',
};

export const ACCESS_INFO_SECTIONS = {
  BUSINESS_OUTCOME: 'BUSINESS_OUTCOME',
  MILESTONE: 'MILESTONE',
  RISK: 'RISK',
  BUDGET: 'BUDGET',
  BILLING_SCHEDULE: 'BILLING_SCHEDULE',
  FINANCE: 'FINANCE',
  EVM: 'EVM',
  CUSTOMER_REPORT: 'CUSTOMER_REPORT',
  SDLC: 'SDLC',
  TEAM: 'TEAM',
  TEAM_PLANNED_LEAVE: 'TEAM_PLANNED_LEAVE',
  PROJECT_SUMMARY: 'PROJECT_SUMMARY',
  PROJECT_TIMELINE_ENTRY: 'PROJECT_TIMELINE_ENTRY',
  RECIPIENT: 'RECIPIENT',
  UPCOMING_CHANGES: 'UPCOMING_CHANGES',
  WASTE: 'WASTE',
  HEALTH: 'HEALTH',
  CRITICAL_PERIOD: 'CRITICAL_PERIOD',
  CONTRACT_RENEWAL: 'CONTRACT_RENEWAL',
  CHANGE_REQUEST: 'CHANGE_REQUEST',
  PROJECT_DIMENSION: 'PROJECT_DIMENSION',
  CLIENT_CONFIRMATION: 'CLIENT_CONFIRMATION',
  PROJECT_VIEW_NAVIGATION: 'PROJECT_VIEW_NAVIGATION',
  ASSUMPTION: 'ASSUMPTION',
  STAKEHOLDER: 'STAKEHOLDER',
  CONTROL_PLANE: 'CONTROL_PLANE',
  ENGINEERING: 'ENGINEERING',
  CLIENT_PORTAL: 'CLIENT_PORTAL',
};

export const ACCESS_INFO_ACTIONS = {
  VIEW: 'VIEW',
  MANAGE: 'MANAGE',
  PARTIAL_MANAGE: 'PARTIAL_MANAGE',
  REVIEW: 'REVIEW',
  SWITCH_TO_STAFFING_VIEW: 'SWITCH_TO_STAFFING_VIEW',
  SWITCH_TO_PROJECT_VIEW: 'SWITCH_TO_PROJECT_VIEW',
};
